import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AppDialog from "@/components/common/AppDialog";
import { useKpiTargetsStore } from "../hooks";
import {
  getDisclosureFrequency,
  getDisclosureIdentifier,
  getSuggestedBaseline,
} from "../utils/disclosure-utils";
import MetricCardHeader from "./MetricCardHeader";
import MetricCardAnnualInput from "./MetricCardAnnualInput";
import MetricCardPeriodicInputs from "./MetricCardPeriodicInputs";
import MetricCardFooter from "./MetricCardFooter";

/**
 * MetricCard - Main component for displaying and editing disclosure targets
 */
const MetricCard = observer(({ disclosure }) => {
  const { t } = useTran();
  const {
    clearTarget,
    getTarget,
    selectedYear,
    setAnnualTarget,
    setPeriodTarget,
  } = useKpiTargetsStore();
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const disclosureId = getDisclosureIdentifier(disclosure);
  const target = getTarget(disclosureId);
  const frequency = getDisclosureFrequency(disclosure);
  const baseline = getSuggestedBaseline(disclosure);

  const handleAnnualChange = (event) => {
    setAnnualTarget(disclosure, event.target.value);
  };

  const handlePeriodChange = (index, value) => {
    setPeriodTarget(disclosure, index, value);
  };

  const handleClear = () => {
    clearTarget(disclosureId);
    setConfirmOpen(false);
  };

  return (
    <>
      <Card className="overflow-hidden border-border/70 bg-card/95 transition-shadow hover:shadow-lg">
        <MetricCardHeader
          disclosure={disclosure}
          frequency={frequency}
          baseline={baseline}
          expanded={expanded}
          onToggle={() => setExpanded((prev) => !prev)}
        />

        <CardContent className="space-y-4 pt-4">
          <MetricCardAnnualInput
            selectedYear={selectedYear}
            target={target}
            disclosure={disclosure}
            baseline={baseline}
            onAnnualChange={handleAnnualChange}
            onDelete={() => setConfirmOpen(true)}
          />

          {expanded ? (
            <MetricCardPeriodicInputs
              frequency={frequency}
              target={target}
              disclosure={disclosure}
              onPeriodChange={handlePeriodChange}
            />
          ) : null}

          <MetricCardFooter disclosure={disclosure} />
        </CardContent>
      </Card>

      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("Delete target?")}
        description={t(
          "This will remove the annual and period targets for this metric.",
        )}
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button type="button" variant="destructive" onClick={handleClear}>
              {t("Delete")}
            </Button>
          </>
        }
      />
    </>
  );
});

export default MetricCard;
